import { HStack, Icon, Stack, Text, useRadioGroup } from '@chakra-ui/react';
import { OfficeBuildingIcon, UserIcon } from '@heroicons/react/outline';
import { useTranslations } from 'use-intl';

import { IdentityType } from '@blockpulse3/graphql/hooks';
import { RadioButton } from '@blockpulse3/ui/commons';

import { ICrowdfundingSubscribers, IFundraisingSubscriber } from '../types';
import {
  CrowdfundingLegalSubscriberInformationsForm,
  CrowdfundingLegalSubscriberInformationsFormProps,
  LegalSubscriberInformationsForm,
  LegalSubscriberInformationsFormProps,
} from './LegalSubscriberInformationsForm';
import {
  CrowdfundingNaturalSubscriberInformationsForm,
  CrowdfundingNaturalSubscriberInformationsFormProps,
  NaturalSubscriberInformationsForm,
  NaturalSubscriberInformationsFormProps,
} from './NaturalSubscriberInformationsForm';

type Props<T> = {
  defaultValues: IFundraisingSubscriber | ICrowdfundingSubscribers;
  onSubmit: (subscriber: T) => void;
  onClose: () => void;
  isCrowdfunding?: boolean;
  isEditing: boolean;
};

type LegalFormProps =
  | CrowdfundingLegalSubscriberInformationsFormProps
  | LegalSubscriberInformationsFormProps;
type NaturalFormProps =
  | CrowdfundingNaturalSubscriberInformationsFormProps
  | NaturalSubscriberInformationsFormProps;

/**
 * SubscriberInformationsForm.
 * Form content of the FundraisingSubscribersModal. Render Legal or Natural version of the form.
 *
 * @param {Props}
 * @returns {JSX.Element}
 */
export function SubscriberInformationsForm<
  T extends IFundraisingSubscriber | ICrowdfundingSubscribers,
>({ isCrowdfunding = false, isEditing, defaultValues, onClose, onSubmit }: Props<T>): JSX.Element {
  const i18nSubscriberInformationsForm = useTranslations('SubscriberInformationsForm');

  const { value, getRootProps, getRadioProps } = useRadioGroup({
    name: 'identityType',
    defaultValue: defaultValues.isLegal ? IdentityType.COMPANY : IdentityType.INDIVIDUAL,
  });

  const isLegal = value === IdentityType.COMPANY;

  const handleFormSubmit = (data: T['subscriber']): void => {
    onSubmit({ isLegal, subscriber: data } as T);
    onClose();
  };

  const LegalForm = ({ ...props }: LegalFormProps): JSX.Element => {
    return isCrowdfunding ? (
      <CrowdfundingLegalSubscriberInformationsForm
        {...(props as CrowdfundingLegalSubscriberInformationsFormProps)}
      />
    ) : (
      <LegalSubscriberInformationsForm {...(props as LegalSubscriberInformationsFormProps)} />
    );
  };

  const NaturalForm = ({ ...props }: NaturalFormProps): JSX.Element => {
    return isCrowdfunding ? (
      <CrowdfundingNaturalSubscriberInformationsForm
        {...(props as CrowdfundingNaturalSubscriberInformationsFormProps)}
      />
    ) : (
      <NaturalSubscriberInformationsForm {...(props as NaturalSubscriberInformationsFormProps)} />
    );
  };

  return (
    <Stack spacing="4">
      <Stack direction={{ base: 'column', md: 'row' }}>
        <Stack direction={{ base: 'column', md: 'row' }} spacing="4" w="full" {...getRootProps()}>
          <RadioButton
            w="50%"
            {...getRadioProps({ value: IdentityType.COMPANY })}
            isDisabled={isEditing}
          >
            <HStack
              alignItems="center"
              color={isEditing ? 'gray.400' : 'gray.900'}
              data-cy="company-subscriber"
              spacing="3"
            >
              <Icon as={OfficeBuildingIcon} boxSize="24px" />
              <Text>{i18nSubscriberInformationsForm('RadioLegal')}</Text>
            </HStack>
          </RadioButton>
          <RadioButton
            w="50%"
            {...getRadioProps({ value: IdentityType.INDIVIDUAL })}
            isDisabled={isEditing}
          >
            <HStack
              alignItems="center"
              color={isEditing ? 'gray.400' : 'gray.900'}
              data-cy="individual-subscriber"
              spacing="3"
            >
              <Icon as={UserIcon} boxSize="24px" />
              <Text>{i18nSubscriberInformationsForm('RadioNatural')}</Text>
            </HStack>
          </RadioButton>
        </Stack>
      </Stack>
      {isLegal ? (
        <LegalForm
          defaultValues={defaultValues.subscriber as LegalFormProps['defaultValues']}
          onSubmit={handleFormSubmit}
        />
      ) : (
        <NaturalForm
          defaultValues={defaultValues.subscriber as NaturalFormProps['defaultValues']}
          onSubmit={handleFormSubmit}
        />
      )}
    </Stack>
  );
}
